import { ChangeDetectorRef, Component, inject, input, OnInit, signal } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ISection, IDocumentType, ISeoDocument } from '../../../../core/models/documents.model';
import { dynamicComponents } from '../../../../core/widget/widget-registry';
import { NgComponentOutlet, DatePipe } from '@angular/common';
import { OrderSelectionService } from '../../../../core/services/order-selection.service';
import { computed } from '@angular/core';
import { OrderProcessingService } from '../../../../core/services/order-processing';
import { OrderPayload, DocumentPayload } from '../../../../core/models/order-payload.model';
import { Router } from '@angular/router';
import { UploadBox } from "../../upload-box/upload-box";
import { GetCountryDocuments } from '../../../../core/services/get-country-documents';

export interface IFaqItem {
    question: string;
    answers?: string;
    answer?: string;
}

import { StateService } from '../../../../core/services/state.service';
import { SeoService } from '../../../../core/services/seo.service';

@Component({
    selector: 'app-make-order',
    imports: [NgComponentOutlet, UploadBox, DatePipe],
    templateUrl: './make-order.html',
    styleUrl: './make-order.scss',
})
export class MakeOrder implements OnInit {
    sectionsRows = input.required<ISection[][]>();
    children = input.required<IDocumentType[]>();
    seoData = input.required<ISeoDocument | ISeoDocument[] | null>();
    featuredImage = input<string>('');
    sidebarSectionInput = input<ISection | null>(null, { alias: 'sidebarSection' });
    

    private stateService = inject(StateService);
    private seoService = inject(SeoService);
    private cdr = inject(ChangeDetectorRef);
    readonly sidebarSection = computed(() => this.sidebarSectionInput() || this.stateService.sidebarSection());

    private sanitizer = inject(DomSanitizer);
    orderSelection = inject(OrderSelectionService);
    private orderProcessing = inject(OrderProcessingService);
    private router = inject(Router);
    private getCountryDocsService = inject(GetCountryDocuments);

    /** Holds fetched price/days data for #totalPrice / #totalDays placeholders */
    private servicePriceData = signal<{ totalPrice: string; totalDays: string } | null>(null);

    // Create an array with 50 items (0 to 50)
    items = Array.from({ length: 51 }, (_, i) => i);

    isExiting = false;
    showDigitalUpload = signal(false);
    hasRegularOrder = false;
    showFdaDigitalModal = signal(false);
    extraUploadFilesCount = signal(0);

    async ngOnInit() {
        this.orderSelection.reset();
        await this.fetchServicePricesIfNeeded();

        if (typeof localStorage !== 'undefined') {
            const savedCart = localStorage.getItem('cart_response');
            if (savedCart) {
                try {
                    const cart = JSON.parse(savedCart);
                    const orderType = cart.data?.orderType || cart.orderType;
                    if (orderType === 'Regular') {
                        this.hasRegularOrder = true;
                        this.showDigitalUpload.set(false);
                    } else if (orderType === 'Digital') {
                        this.showDigitalUpload.set(true);
                    }
                } catch (e) {
                    console.error('Failed to parse cart_response', e);
                }
            }
        }
    }

    /**
     * Scans all section content for #totalPrice / #totalDays placeholders.
     * If found, fetches document prices from the API using the orderId hidden input
     * and stores the result so sortTheMail can replace the placeholders.
     */
    private async fetchServicePricesIfNeeded() {
        const priceData = await this.getCountryDocsService.fetchServicePricesIfNeeded(
            this.sectionsRows(),
            this.seoData()
        );

        if (priceData) {
            this.servicePriceData.set(priceData);
            this.cdr.detectChanges();
        }
    }

    uploadHeadings = computed(() => {
        const docs = [...this.orderSelection.selectedDocs()].sort((a, b) => (Number(a.doc.id) === 29 ? -1 : Number(b.doc.id) === 29 ? 1 : 0));
        const headings: string[] = [];
        let logicalDocIndex = 1;
        
        for (const entry of docs) {
            for (let i = 0; i < entry.quantity; i++) {
                if (Number(entry.doc.id) === 29 && this.extraUploadFilesCount() > 0) {
                    headings.push(`Please upload 1st file for the ${this.getOrdinal(logicalDocIndex)} document`);
                    headings.push(`Please upload 2nd file for the ${this.getOrdinal(logicalDocIndex)} document`);
                } else {
                    headings.push(`Please upload your ${this.getOrdinal(logicalDocIndex)} document`);
                }
                logicalDocIndex++;
            }
        }
        
        return headings;
    });

    private getOrdinal(n: number): string {
        const s = ['th', 'st', 'nd', 'rd'];
        const v = n % 100;
        return n + (s[(v - 20) % 10] || s[v] || s[0]);
    }

    onCountChange(event: Event) {
        const selectElement = event.target as HTMLSelectElement;
        const value = parseInt(selectElement.value, 10);
        if (value > 0) {
            this.isExiting = true;
            setTimeout(() => {
                this.orderSelection.manualDocCount.set(value);
                this.orderSelection.startWithCount.set(true);
                this.orderSelection.fetchDocumentPrices();
                this.isExiting = false;
            }, 600);
        }
    }
    /**
     * Splits a raw CMS HTML string into segments.
     * Each segment is either:
     *   - a plain HTML chunk  → { html: SafeHtml, componentToBootUp: undefined }
     *   - a widget flag token → { html: null,     componentToBootUp: ComponentClass }
     *
     * Driven entirely by the keys in dynamicComponents, so adding a new widget
     * only requires registering it in widget-registry — no template changes.
     */
    sortTheMail(rawText: string) {
        const flagKeys = Object.keys(dynamicComponents);
        const escapedKeys = flagKeys.map(k => k.replace(/\$/g, '\\$'));
        const splitter = new RegExp(`(${escapedKeys.join('|')})`);

        if (rawText.includes("$startOrderWthNoAction")) {
            rawText = rawText.replace("$startOrderWthNoAction", "");
            if (!this.orderSelection.showOrderProcess()) {
                setTimeout(() => {
                    this.orderSelection.showOrderProcess.set(true);
                    this.orderSelection.startWithCount.set(false)
                });
            }
        }

        // Replace #totalPrice / #totalDays with fetched service data
        const priceData = this.servicePriceData();
        if (priceData) {
            rawText = rawText.replace(/#totalPrice/g, priceData.totalPrice);
            rawText = rawText.replace(/#totalDays/g, priceData.totalDays);
        } else {
            rawText = rawText.replace(/#totalPrice/g, '...');
            rawText = rawText.replace(/#totalDays/g, '...');
        }

        return rawText
            .split(splitter)
            .filter(Boolean)
            .map(slice => ({
                html: dynamicComponents[slice]
                    ? null
                    : this.sanitizer.bypassSecurityTrustHtml(slice),
                componentToBootUp: dynamicComponents[slice] ?? null
            }));
    }

    parseFaq(content: any): IFaqItem[] {
        if (!content) return [];
        if (Array.isArray(content)) return content;
        try {
            let parsed = typeof content === 'string' ? JSON.parse(content) : content;
            if (typeof parsed === 'string') {
                parsed = JSON.parse(parsed);
            }
            return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
            console.error('Error parsing FAQ content:', e);
            return [];
        }
    }

    async processOrder(fileIds?: number[]) {
        this.orderSelection.isLoadingPrices.set(true);
        try {
            const hiddenInput = typeof document !== 'undefined' ? document.querySelector<HTMLInputElement>('input.orderId') : null;
            const dotNetCountryId = hiddenInput ? Number(hiddenInput.dataset['dotnetcountryid']) : 0;
            const countryDocumentCaseId = hiddenInput ? Number(hiddenInput.dataset['id']) : 0;

            const dateVal = this.orderSelection.date();
            let formattedDate = '';
            if (dateVal) {
                const day = String(dateVal.getDate()).padStart(2, '0');
                const month = String(dateVal.getMonth() + 1).padStart(2, '0');
                const year = dateVal.getFullYear();
                formattedDate = `${month}/${day}/${year}`;
            }

            const sortedDocs = [...this.orderSelection.selectedDocs()].sort((a, b) => (a.doc.id === 29 ? -1 : b.doc.id === 29 ? 1 : 0));
            
            let documents: DocumentPayload[] = sortedDocs.map(entry => {
                return {
                    countryDocumentCaseId: countryDocumentCaseId,

                    count: entry.quantity,
                    is_expedited: this.orderSelection.hasExp(),
                    "reg-days": formattedDate,
                    "exp-days": formattedDate,
                    ...(fileIds ? { files: fileIds } : {})
                };
            });

            if (documents.length === 0 && this.orderSelection.manualDocCount() !== null) {
                documents = [{
                    countryDocumentCaseId: countryDocumentCaseId,
                    count: this.orderSelection.manualDocCount()!,
                    is_expedited: this.orderSelection.hasExp(),
                    "reg-days": formattedDate,
                    "exp-days": formattedDate,
                    ...(fileIds ? { files: fileIds } : {})
                }];
            }

            const payload: OrderPayload = {
                dotNetCountryId,
                documents,
                order_type: fileIds ? "Digital" : "Regular",
                extra_pages: this.orderSelection.extraPages(),
            };

            const res = await this.orderProcessing.addToCart(payload);
            if (res) {
                this.router.navigate(['/cart']);
            }
        } finally {
            this.orderSelection.isLoadingPrices.set(false);
        }
    }

    onProceed() {
        this.processOrder();
    }

    onHardCopy() {
        this.processOrder();
    }

    onDigitalDocument() {
        if (this.orderSelection.getQuantity(29) > 0) {
            this.showFdaDigitalModal.set(true);
        } else {
            this.showDigitalUpload.set(true);
        }
    }

    onFdaDigitalOneFile() {
        this.showFdaDigitalModal.set(false);
        this.showDigitalUpload.set(true);
    }

    onFdaDigitalTwoFiles() {
        const qty29 = this.orderSelection.getQuantity(29);
        this.extraUploadFilesCount.set(qty29);
        this.showFdaDigitalModal.set(false);
        this.showDigitalUpload.set(true);
    }

    onUploadsCompleted(fileIds: number[]) {
        this.processOrder(fileIds);
    }
}
